import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SECTION_IDS } from "./sectionConfig";

// `?section=<id>` on /request/{order|sell|px-order}/{id} — open the page on that
// section (Dashboard v2 action buttons link here: "Raise PO" →
// ?section=purchase_order, "Invoice" → ?section=invoice, …).
//
// Ids are the SECTION_IDS values (lowercase, e.g. `send_quote`); the constant
// NAME (`SEND_QUOTE`) is accepted too, case-insensitively, because both spellings
// appear in links. Anything else is ignored.
const KNOWN = new Set(Object.values(SECTION_IDS));

export const readSectionParam = (search) => {
  const raw = new URLSearchParams(search || "").get("section");
  if (!raw) return null;
  const id = raw.trim().toLowerCase();
  return KNOWN.has(id) ? id : null;
};

/**
 * Selects the requested section ONCE, the first time it is among the visible
 * sections. If it never becomes visible (the deal is not at that stage, or the
 * plan hides it) nothing happens and the page keeps its normal landing.
 *
 * `appliedRef` is owned by the page (RequestTemplateV2), not by this hook's
 * caller: the shell remounts when the OrderManagementProvider is re-keyed, and a
 * ref local to the shell would re-apply the link and drag the rep back.
 * The page also sets it when the rep picks a section themselves.
 */
export function useSectionDeepLink(visibleSections, onSelect, appliedRef) {
  const { search } = useLocation();
  const wanted = readSectionParam(search);
  const visibleKey = (visibleSections || []).map((s) => s.id).join(",");

  useEffect(() => {
    if (!wanted || !appliedRef || appliedRef.current) return;
    if (!(visibleSections || []).some((s) => s.id === wanted)) return;
    appliedRef.current = true;
    onSelect(wanted);
    // onSelect identity changes every render; the visible set is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, visibleKey]);
}
